/**
 * Users & Roles Management Page
 *
 * Admin-only page for managing tenant users, roles, and invitations.
 * Accessible to Organization.Admin and System.Admin roles.
 *
 * Route: /settings/users | Nav: Settings > Users & Roles
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  Button,
  Badge,
  Tab,
  TabList,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  tokens,
  Spinner,
  MessageBar,
  MessageBarBody,
  type SelectTabEvent,
  type SelectTabData,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  PersonAddRegular,
  ShieldPersonRegular,
  MailRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import {
  TenantMembersList,
  PendingInvitationsList,
  InviteUserForm,
} from '../features/identity';
import { useAvailableRoles } from '../features/identity/hooks';
import { useAuth } from '../features/authentication';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';

type TabValue = 'members' | 'invitations' | 'roles';

export const UsersRolesPage: React.FC = () => {
  const ps = usePageStyles();
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState<TabValue>('members');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const currentTenantId = user?.tenantMemberships?.[0]?.tenantId ?? '';
  const currentUserId = user?.id;

  const { data: rolesInfo, isLoading: rolesLoading } = useAvailableRoles(currentTenantId);

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const isAdmin = user?.roles?.some(r => r === 'System.Admin' || r === 'Organization.Admin') ?? false;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Users & Roles"
        subtitle="Manage team members, role assignments, and invitations"
        icon={<PeopleRegular />}
      />

      {!currentTenantId && (
        <MessageBar intent="warning">
          <MessageBarBody>
            No tenant membership found. User management requires an active tenant context.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Summary Stats */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <PeopleRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Team Members</Body2>
              <Title2>—</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldPersonRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Available Roles</Body2>
              <Title2>{rolesLoading ? '...' : rolesInfo?.length ?? 6}</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <MailRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Pending Invitations</Body2>
              <Title2>—</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Your Role</Body2>
              <Title2 style={{ fontSize: '1rem' }}>
                {user?.roles?.find(r => r === 'System.Admin') ? 'System Admin' :
                 user?.roles?.find(r => r === 'Organization.Admin') ? 'Org Admin' : 'User'}
              </Title2>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs + Invite Button */}
      <Card>
        <div className={ps.cardBody}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM }}>
            <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
              <Tab value="members" icon={<PeopleRegular />}>Team Members</Tab>
              <Tab value="invitations" icon={<MailRegular />}>Invitations</Tab>
              <Tab value="roles" icon={<ShieldPersonRegular />}>Role Definitions</Tab>
            </TabList>

            {isAdmin && (
              <Dialog
                open={isInviteDialogOpen}
                onOpenChange={(_, data) => setIsInviteDialogOpen(data.open)}
              >
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="primary" icon={<PersonAddRegular />}>
                    Invite User
                  </Button>
                </DialogTrigger>
                <DialogSurface>
                  <DialogBody>
                    <DialogTitle>Invite New Team Member</DialogTitle>
                    <DialogContent>
                      <InviteUserForm
                        tenantId={currentTenantId}
                        onSuccess={() => setIsInviteDialogOpen(false)}
                        onCancel={() => setIsInviteDialogOpen(false)}
                      />
                    </DialogContent>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
            )}
          </div>

          {/* Tab Content */}
          {selectedTab === 'members' && (
            <TenantMembersList
              tenantId={currentTenantId}
              currentUserId={currentUserId}
              canManageRoles={isAdmin}
              canRemoveMembers={isAdmin}
            />
          )}

          {selectedTab === 'invitations' && (
            <PendingInvitationsList
              tenantId={currentTenantId}
              showAllStatuses
            />
          )}

          {selectedTab === 'roles' && (
            <RoleDefinitionsPanel roles={rolesInfo} isLoading={rolesLoading} />
          )}
        </div>
      </Card>
    </div>
  );
};

interface RoleDefinitionsPanelProps {
  roles?: Array<{ name: string; displayName: string; description: string }>;
  isLoading: boolean;
}

const RoleDefinitionsPanel: React.FC<RoleDefinitionsPanelProps> = ({ roles, isLoading }) => {
  const ps = usePageStyles();

  // Fallback definitions if API hasn't loaded yet
  const fallbackRoles = [
    { name: 'Owner', displayName: 'Owner', description: 'Full administrative access including billing and tenant deletion' },
    { name: 'Admin', displayName: 'Administrator', description: 'Manage users, settings, and all operational aspects' },
    { name: 'SecurityEngineer', displayName: 'Security Engineer', description: 'Configure security policies, scans, and resolve findings' },
    { name: 'Developer', displayName: 'Developer', description: 'Execute scans and manage findings for assigned projects' },
    { name: 'Auditor', displayName: 'Auditor', description: 'Read-only access for compliance auditing and reporting' },
    { name: 'Viewer', displayName: 'Viewer', description: 'Read-only access to dashboards and reports' },
  ];

  const displayRoles = roles ?? fallbackRoles;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
        <Spinner size="medium" label="Loading role definitions..." />
      </div>
    );
  }

  const getRoleBadgeColor = (name: string): 'important' | 'brand' | 'informative' | 'subtle' => {
    switch (name) {
      case 'Owner': return 'important';
      case 'Admin': return 'brand';
      case 'SecurityEngineer':
      case 'Developer': return 'informative';
      default: return 'subtle';
    }
  };

  return (
    <div className={ps.rowList}>
      {displayRoles.map((role) => (
        <div key={role.name} className={ps.rowItem}>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flex: 1 }}>
            <Badge appearance="filled" color={getRoleBadgeColor(role.name)} style={{ minWidth: '120px', justifyContent: 'center' }}>
              {role.displayName}
            </Badge>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
              <Body1 style={{ fontWeight: 500 }}>{role.displayName}</Body1>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{role.description}</Body2>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
